import type {
  PushChangeInput,
  PullResponse,
  PushResponse,
  SyncMetaResponse,
} from '../../../shared/api/types';
import { apiRequest, isNetworkError } from '../../../shared/api/client';
import { PULL_PAGE_LIMIT, SYNC_BATCH_LIMIT } from '../../../shared/lib/constants';
import { db } from '../db/database';
import { clearLocalEntities, applyPullChange, applyServerEntity } from '../db/entities.repo';
import {
  countUnsynced,
  clearOutbox,
  deleteOutboxEntries,
  listOutboxBatch,
  markOutboxInflight,
  markOutboxPending,
  recoverStaleInflight,
} from '../db/outbox.repo';
import {
  readEpoch,
  readLastRevision,
  writeEpoch,
  writeLastRevision,
} from '../db/meta.repo';
import { showToast } from '../../../shared/ui/toast';
import { SyncWebSocket, resolveWsUrl } from './ws-client';
import { computeBackoffMs } from './backoff';

export interface SyncEngineStatus {
  online: boolean;
  syncing: boolean;
  pendingCount: number;
  lastSyncAt: number | null;
  lastError: string | null;
  wsStatus: 'connecting' | 'open' | 'closed' | 'idle';
}

type StatusListener = (status: SyncEngineStatus) => void;

const INITIAL_STATUS: SyncEngineStatus = {
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  syncing: false,
  pendingCount: 0,
  lastSyncAt: null,
  lastError: null,
  wsStatus: 'idle',
};

/**
 * Фоновый воркер синхронизации: pull/push + WS + сеть.
 * Все локальные изменения уже лежат в Dexie/outbox до вызова syncNow().
 */
export class SyncEngine {
  private status: SyncEngineStatus = { ...INITIAL_STATUS };
  private listeners = new Set<StatusListener>();
  private userId: string | null = null;
  private ws: SyncWebSocket | null = null;
  private stopped = true;
  private pullInFlight = false;
  private pushInFlight = false;
  private pushAttempt = 0;
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private pullTimer: ReturnType<typeof setTimeout> | null = null;
  private bound = false;

  getStatus(): SyncEngineStatus {
    return this.status;
  }

  subscribe(listener: StatusListener): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private setStatus(patch: Partial<SyncEngineStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const listener of this.listeners) {
      listener(this.status);
    }
  }

  async refreshPendingCount(): Promise<void> {
    const pendingCount = await countUnsynced();
    this.setStatus({ pendingCount });
  }

  async start(userId: string): Promise<void> {
    this.userId = userId;
    this.stopped = false;
    this.bindNetworkEvents();
    await recoverStaleInflight();
    await this.refreshPendingCount();
    this.setStatus({ online: navigator.onLine });
    await this.syncNow();
    this.connectWs();
  }

  stop(): void {
    this.stopped = true;
    this.unbindNetworkEvents();
    this.ws?.close();
    this.ws = null;
    if (this.pushTimer !== null) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }
    if (this.pullTimer !== null) {
      clearTimeout(this.pullTimer);
      this.pullTimer = null;
    }
    this.userId = null;
    this.setStatus({ wsStatus: 'idle', syncing: false });
  }

  async syncNow(): Promise<void> {
    if (this.stopped || this.userId === null) {
      return;
    }
    if (!navigator.onLine) {
      this.setStatus({ online: false });
      await this.refreshPendingCount();
      return;
    }

    this.setStatus({ online: true, syncing: true, lastError: null });
    try {
      await this.pullAll();
      await this.pushOutbox();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ошибка синхронизации';
      this.setStatus({ lastError: message });
    } finally {
      this.setStatus({ syncing: false, lastSyncAt: Date.now() });
      await this.refreshPendingCount();
    }
  }

  schedulePull(delayMs = 0): void {
    if (this.stopped || this.userId === null) {
      return;
    }
    if (this.pullTimer !== null) {
      clearTimeout(this.pullTimer);
    }
    this.pullTimer = setTimeout(() => {
      this.pullTimer = null;
      void this.syncNow();
    }, delayMs);
  }

  private connectWs(): void {
    if (this.stopped || this.userId === null || !navigator.onLine) {
      return;
    }
    if (this.ws !== null) {
      this.ws.connect();
      return;
    }
    this.ws = new SyncWebSocket({
      getWsUrl: resolveWsUrl,
      onStatus: (wsStatus) => {
        this.setStatus({ wsStatus });
      },
      onMessage: (message) => {
        if (message.type === 'changes') {
          this.schedulePull(0);
        }
      },
      onOpen: () => {
        this.schedulePull(0);
      },
    });
    this.ws.connect();
  }

  private bindNetworkEvents(): void {
    if (this.bound || typeof window === 'undefined') {
      return;
    }
    this.bound = true;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('focus', this.handleFocus);
  }

  private unbindNetworkEvents(): void {
    if (!this.bound || typeof window === 'undefined') {
      return;
    }
    this.bound = false;
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('focus', this.handleFocus);
  }

  private readonly handleOnline = (): void => {
    this.setStatus({ online: true, lastError: null });
    this.pushAttempt = 0;
    this.connectWs();
    this.schedulePull(0);
  };

  private readonly handleOffline = (): void => {
    this.setStatus({ online: false });
    this.ws?.close();
  };

  private readonly handleVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      this.schedulePull(200);
    }
  };

  private readonly handleFocus = (): void => {
    this.schedulePull(200);
  };

  private async pullAll(): Promise<void> {
    if (this.pullInFlight || this.userId === null) {
      return;
    }
    this.pullInFlight = true;
    const userId = this.userId;

    try {
      let since = await readLastRevision();
      let epoch = await readEpoch();
      let epochResetDone = false;
      let recoveryAttempts = 0;

      if (epoch.length === 0) {
        const meta = await apiRequest<SyncMetaResponse>('/api/sync/meta');
        epoch = meta.epoch;
        await writeEpoch(epoch);
        since = 0;
        await writeLastRevision(0);
      }

      for (;;) {
        // Локально нет живых записей + revision > 0 → полный догон (до 2 раз).
        if (!epochResetDone && recoveryAttempts < 2 && since > 0) {
          const [tasks, tags, projects] = await Promise.all([
            db.tasks.toArray(),
            db.tags.toArray(),
            db.projects.toArray(),
          ]);
          const liveTasks = tasks.filter((t) => t.deleted_at == null);
          const liveTags = tags.filter((t) => t.deleted_at == null);
          const liveProjects = projects.filter((t) => t.deleted_at == null);
          if (liveTasks.length === 0 && liveTags.length === 0 && liveProjects.length === 0) {
            recoveryAttempts += 1;
            since = 0;
            await writeLastRevision(0);
          }
        }

        const page = await apiRequest<PullResponse>(
          `/api/sync/pull?since=${since}&limit=${PULL_PAGE_LIMIT}`,
        );

        if (epoch.length > 0 && page.epoch !== epoch) {
          // Смена эпохи: сначала УСПЕШНО забрали данные с новой эпохи,
          // только потом чистим локальное — иначе при ошибке сети база пустеет.
          const fresh = await apiRequest<PullResponse>(
            `/api/sync/pull?since=0&limit=${PULL_PAGE_LIMIT}`,
          );
          if (fresh.epoch === page.epoch || fresh.epoch.length > 0) {
            const pendingBefore = await countUnsynced();
            await clearLocalEntities();
            await clearOutbox();
            await writeLastRevision(0);
            epoch = fresh.epoch;
            await writeEpoch(epoch);
            since = 0;
            epochResetDone = true;
            if (pendingBefore > 0) {
              showToast(
                'Очередь изменений очищена после восстановления данных на сервере',
                'info',
              );
            }
            await this.applyPullPage(fresh, userId);
            since = Math.max(since, fresh.latest_revision);
            await writeLastRevision(since);
            if (!fresh.has_more) {
              break;
            }
            continue;
          }
          // Не удалось получить свежую эпоху — не чистим, повторим позже.
          break;
        }

        if (epoch.length === 0) {
          epoch = page.epoch;
          await writeEpoch(epoch);
        }

        await this.applyPullPage(page, userId);
        since = Math.max(since, page.latest_revision);
        await writeLastRevision(since);
        epoch = page.epoch;
        await writeEpoch(epoch);

        if (!page.has_more) {
          break;
        }
      }
    } finally {
      this.pullInFlight = false;
    }
  }

  private async applyPullPage(page: PullResponse, userId: string): Promise<void> {
    let applied = 0;
    let failed = 0;
    for (const change of page.changes) {
      try {
        await applyPullChange(change, userId);
        applied += 1;
      } catch (error) {
        failed += 1;
        console.error(
          JSON.stringify({
            level: 'error',
            event: 'pull_apply_failed',
            type: change.type,
            entity_id: change.entity_id,
            message: error instanceof Error ? error.message : String(error),
          }),
        );
      }
    }
    if (page.changes.length > 0) {
      console.info(
        JSON.stringify({
          level: 'info',
          event: 'pull_applied',
          received: page.changes.length,
          applied,
          failed,
          since_latest: page.latest_revision,
        }),
      );
    }
  }

  private async pushOutbox(): Promise<void> {
    if (this.pushInFlight || this.userId === null) {
      return;
    }

    for (;;) {
      const batch = await listOutboxBatch(SYNC_BATCH_LIMIT);
      if (batch.length === 0) {
        this.pushAttempt = 0;
        return;
      }

      this.pushInFlight = true;
      const clientOpIds = batch.map((entry) => entry.client_op_id);
      const batchOpId = clientOpIds[0] ?? undefined;

      try {
        await markOutboxInflight(clientOpIds);

        const changes: PushChangeInput[] = batch.map((entry, index) => {
          const change: PushChangeInput = {
            entity_type: entry.entity_type,
            entity_id: entry.entity_id,
            updated_at: entry.updated_at,
          };
          if (index === 0 && batchOpId !== undefined) {
            change.client_op_id = batchOpId;
          }
          if (entry.deleted) {
            change.deleted = true;
          }
          if (entry.payload !== null) {
            change.payload = entry.payload;
          }
          return change;
        });

        const result = await apiRequest<PushResponse>('/api/sync/push', {
          method: 'POST',
          body: { changes },
        });

        // Идемпотентность сервера — по client_op_id пачки. Удаляем отправленные записи.
        await deleteOutboxEntries(clientOpIds);

        for (const item of result.results) {
          if (item.entity !== null && this.userId !== null) {
            await applyServerEntity(
              item.entity_type,
              item.entity_id,
              item.entity,
              item.server_revision,
              false,
              this.userId,
              'push',
            );
          }
        }

        await writeLastRevision(Math.max(await readLastRevision(), result.latest_revision));
        if (result.epoch.length > 0) {
          await writeEpoch(result.epoch);
        }

        this.pushAttempt = 0;
        this.setStatus({ lastError: null });
      } catch (error) {
        if (isNetworkError(error)) {
          this.setStatus({ online: false });
          await markOutboxPending(clientOpIds, 'Нет соединения');
          this.pushAttempt += 1;
          this.schedulePushRetry();
          return;
        }

        // 401 — сессия невалидна; engine не делает logout сам (это auth-слой).
        if (error instanceof Error && 'status' in error && (error as { status: number }).status === 401) {
          await markOutboxPending(clientOpIds, 'Сессия истекла');
          this.setStatus({ lastError: 'Сессия истекла', syncing: false });
          return;
        }

        // Ошибки валидации/сервера: помечаем попытку, но не роняем весь цикл навсегда.
        const message = error instanceof Error ? error.message : 'Ошибка отправки';
        await markOutboxPending(clientOpIds, message);
        this.pushAttempt += 1;
        this.setStatus({ lastError: message });
        this.schedulePushRetry();
        return;
      } finally {
        this.pushInFlight = false;
      }
    }
  }

  private schedulePushRetry(): void {
    if (this.stopped) {
      return;
    }
    if (this.pushTimer !== null) {
      clearTimeout(this.pushTimer);
    }
    const delay = computeBackoffMs(Math.max(1, this.pushAttempt));
    this.pushTimer = setTimeout(() => {
      this.pushTimer = null;
      void this.pushOutbox();
    }, delay);
  }
}

export const syncEngine = new SyncEngine();
